import {
  Camera, Mic, UtensilsCrossed, Award, Users, Sparkles, GraduationCap, HandHeart, Trophy,
} from "lucide-react";

const EXPERIENCES = [
  { icon: Camera, label: "Red Carpet Experience", w: 5 },
  { icon: Mic, label: "Live Media Coverage", w: 4 },
  { icon: UtensilsCrossed, label: "Hors d'Oeuvres & Themed Beverages", w: 3 },
  { icon: Users, label: "Executive Networking", w: 4 },
  { icon: Award, label: "Industry Awards", w: 5 },
  { icon: Sparkles, label: "VIP Exclusive Experience", w: 3 },
  { icon: Sparkles, label: "Keynote Speakers", w: 4 },
  { icon: GraduationCap, label: "Scholarship Presentations", w: 5 },
  { icon: HandHeart, label: "Strategic Announcements", w: 4 },
  { icon: Trophy, label: "Community Impact Awards", w: 3 },
];

const YEARS = [
  { year: "2023", guests: 200, status: "Sold Out", note: "1st Annual · Inaugural Gala" },
  { year: "2024", guests: 250, status: "Sold Out", note: "2nd Annual · Expanded Impact Gala" },
  { year: "2025", guests: null, status: "Strategic Pause", note: "Workforce & community programs year" },
  { year: "2026", guests: 50, status: "Exclusive Evening", note: "3rd Annual · Invitation-Only Reception · December 10" },
];

function ArchiveCard({ g, year, testId }) {
  return (
    <figure
      data-testid={testId}
      className="group relative overflow-hidden border border-white/10 bg-aawta-charcoal aspect-[4/5]"
    >
      <img
        src={g.url}
        alt={g.caption}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2200ms] ease-out group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-aawta-jet via-aawta-jet/30 to-transparent" />
      <figcaption className="absolute bottom-0 left-0 right-0 p-5">
        <p className="font-display text-lg md:text-xl text-white leading-tight">{g.caption}</p>
        <p className="text-overline text-aawta-pink mt-2">{g.sub}</p>
      </figcaption>
      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1">
        <span className="w-1.5 h-1.5 bg-aawta-pink rounded-full" /> REAL · {year}
      </span>
    </figure>
  );
}

export function WhyMatters() {
  return (
    <section
      id="about"
      data-testid="why-matters-section"
      className="relative py-24 md:py-32 scroll-mt-nav overflow-hidden"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5 relative space-y-4">
          <div className="aspect-[4/5] relative overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/qiqmz614_IMG_1085.jpeg"
              alt="2024 Gala keynote speaker"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-aawta-blue/40 to-transparent mix-blend-multiply" />
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-aawta-pink/90 text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1">
              <span className="w-1.5 h-1.5 bg-white rounded-full" /> 2023 · KEYNOTE
            </span>
          </div>
          <div className="aspect-[16/10] relative overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/pqrs2uag_IMG_7092.jpg"
              alt="2023 Gala audience raising a toast"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-aawta-jet/60 via-transparent to-transparent" />
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-black/70 backdrop-blur border border-white/15 text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1">
              <span className="w-1.5 h-1.5 bg-aawta-pink rounded-full animate-pulse" /> 2023 · THE TOAST
            </span>
            <p className="absolute bottom-3 right-4 text-overline text-white/70">200 GUESTS · SOLD OUT</p>
          </div>
          <div className="absolute -bottom-6 -right-6 glass-panel p-6 hidden md:block max-w-[240px]">
            <p className="font-display text-3xl text-aawta-pink">50</p>
            <p className="text-overline text-white/70 mt-1">Invited Guests · Decatur 2026</p>
          </div>
        </div>

        <div className="md:col-span-7">
          <p className="text-overline text-aawta-pink mb-5">WHY THIS EVENT MATTERS</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] font-light leading-[1.05] text-white tracking-tight mb-6">
            More than a reception.<br />
            <span className="italic text-white/70">A movement in motion.</span>
          </h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-5">
            After two sold-out galas in 2023 and 2024, the African American Women Trucking
            Association (AAWTA) returns in 2026 with an exclusive evening &mdash; the
            Power to Drive Change Leadership &amp; Impact Reception &mdash; limited to just 50
            invited guests for a focused, high-impact gathering.
          </p>
          <p className="text-white/60 text-base leading-relaxed mb-8">
            Proceeds fuel our fundraising mission across workforce development, leadership
            advancement, scholarships, youth engagement, and industry impact programs that
            move trucking, logistics, transportation, and supply chain forward.
          </p>
          <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { k: "$85K", v: "2026 Goal" },
              { k: "10+", v: "Award Categories" },
              { k: "1 Night", v: "Lifetime Impact" },
            ].map((s) => (
              <div key={s.v} className="bg-aawta-jet p-5">
                <p className="font-display text-3xl text-white">{s.k}</p>
                <p className="text-overline text-white/50 mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function EventExperience() {
  return (
    <section
      id="experience"
      data-testid="event-experience-section"
      className="relative py-24 md:py-32 bg-aawta-charcoal scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <p className="text-overline text-aawta-pink mb-4">THE EVENING</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight">
              An experience<br />curated for <em className="italic text-aawta-pink">impact</em>.
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm leading-relaxed">
            Ten curated moments of an exclusive reception &mdash; from the step-and-repeat to
            scholarship presentations &mdash; woven into one unforgettable evening of recognition
            and community.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[160px] md:auto-rows-[200px] gap-3">
          {EXPERIENCES.map((e, i) => {
            const Icon = e.icon;
            const colSpan = `md:col-span-${e.w}`;
            return (
              <div
                key={e.label}
                data-testid={`experience-card-${i}`}
                className={`relative bg-aawta-jet border border-white/10 p-6 flex flex-col justify-between overflow-hidden group hover:border-aawta-pink/50 transition-all duration-500 ${colSpan} col-span-2`}
              >
                <Icon className="text-aawta-pink group-hover:scale-110 transition-transform duration-500" size={28} />
                <div>
                  <p className="font-display text-xl md:text-2xl text-white leading-tight">{e.label}</p>
                  <p className="text-overline text-white/30 mt-2">0{i + 1}</p>
                </div>
                <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-aawta-pink/0 group-hover:bg-aawta-pink/15 blur-3xl transition-all duration-700" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PastSuccess() {
  const GALLERY_2023 = [
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/kwje9279_IMG_2922.jpeg",
      caption: "Red Carpet & Step-and-Repeat",
      sub: "Sponsor wall · Atlanta · 2023",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/qiqmz614_IMG_1085.jpeg",
      caption: "Keynote Moment",
      sub: "Industry Leadership Address · 2023",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/5yc8p4g9_IMG_2480.jpg",
      caption: "Inside the Ballroom",
      sub: "200 Guests · Sold Out · 2023",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/l6x6ptrm_IMG_2959.jpeg",
      caption: "The Menu",
      sub: "Fine Dining Experience · 2023",
    },
  ];

  const GALLERY_2024 = [
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/1a4e73w7_IMG_6277.jpg",
      caption: "Red Carpet Arrival",
      sub: "On-Camera Interview · 2024",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/31t3cqkb_IMG_9982.jpeg",
      caption: "Live Media Coverage",
      sub: "Press Interview · Sponsor Wall · 2024",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/wdyobnoq_36792.jpg",
      caption: "Cocktail Reception",
      sub: "Welcome Hour · 2024",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/guba1kbf_IMG_4938.jpg",
      caption: "Awards on Stage",
      sub: "250 Guests · Recognition Ceremony · 2024",
    },
    {
      url: "https://customer-assets.emergentagent.com/job_aawta-gala/artifacts/6g52kih6_IMG_1889%20%282%29.JPG",
      caption: "Live Entertainment",
      sub: "House Band · 2024",
    },
  ];

  return (
    <section
      data-testid="past-success-section"
      className="relative py-24 md:py-32 scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <p className="text-overline text-aawta-pink mb-4">EVENT HISTORY</p>
          <h2 className="font-display text-4xl md:text-5xl font-light text-white tracking-tight leading-tight">
            Two galas.<br /> Two sell-outs.<br /> One <em className="italic text-aawta-pink">intimate</em> reception.
          </h2>
          <p className="text-white/60 mt-6 text-sm leading-relaxed">
            After back-to-back sold-out galas and a 2025 spent deepening our workforce and
            partnership programs, Power to Drive Change returns in 2026 as an exclusive
            Leadership &amp; Impact Reception &mdash; limited to just 50 invited guests.
          </p>
        </div>
        <div className="md:col-span-8 relative">
          <div className="absolute left-3 md:left-5 top-2 bottom-2 w-px bg-gradient-to-b from-aawta-pink via-aawta-blue to-transparent" />
          <div className="space-y-10">
            {YEARS.map((y, i) => {
              const isCurrent = y.year === "2026";
              const isPause = y.guests === null;
              return (
                <div key={y.year} data-testid={`past-year-${y.year}`} className="relative pl-12 md:pl-16">
                  <div
                    className={`absolute left-0 top-2 w-7 h-7 rounded-full border-2 ${
                      isCurrent
                        ? "border-aawta-pink animate-glow-pulse"
                        : isPause
                        ? "border-white/25 border-dashed"
                        : "border-white/50"
                    } bg-aawta-jet flex items-center justify-center`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        isCurrent ? "bg-aawta-pink" : isPause ? "bg-white/30" : "bg-white/60"
                      }`}
                    />
                  </div>
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <p
                      className={`font-display text-5xl md:text-6xl font-light ${
                        isPause ? "text-white/40" : "text-white"
                      }`}
                    >
                      {y.year}
                    </p>
                    <p
                      className={`text-overline ${
                        isCurrent ? "text-aawta-pink" : isPause ? "text-white/40" : "text-white/50"
                      }`}
                    >
                      {y.status}
                    </p>
                  </div>
                  <p className={`font-mono text-sm mt-2 ${isPause ? "text-white/40" : "text-white/60"}`}>
                    {y.guests ? `${y.guests}${y.year === "2026" ? " Invited Guests" : " Guests"}` : "No Gala · Workforce & Programs Focus"}
                  </p>
                  <p className="text-xs text-white/40 mt-1">{y.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Archive — 2023 Inaugural Gala */}
      <div data-testid="gallery-2023" className="max-w-[1500px] mx-auto px-6 md:px-12 mt-20 md:mt-24">
        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <div>
            <p className="text-overline text-aawta-pink mb-3">FROM THE ARCHIVE</p>
            <h3 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight">
              Inside the 2023 Gala
            </h3>
          </div>
          <p className="text-white/50 text-sm max-w-md">
            A glimpse into our sold-out 2023 inaugural evening &mdash; the same spirit returns
            December 10, 2026 as an exclusive 50-guest reception.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {GALLERY_2023.map((g, i) => (
            <ArchiveCard key={g.url} g={g} year="2023" testId={`gallery-2023-${i}`} />
          ))}
        </div>
      </div>

      {/* Archive — 2024 Second Annual Gala */}
      <div data-testid="gallery-2024" className="max-w-[1500px] mx-auto px-6 md:px-12 mt-16 md:mt-20">
        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <div>
            <p className="text-overline text-aawta-pink mb-3">FROM THE ARCHIVE</p>
            <h3 className="font-display text-3xl md:text-4xl font-light text-white tracking-tight">
              Inside the 2024 Gala
            </h3>
          </div>
          <p className="text-white/50 text-sm max-w-md">
            Our second sold-out evening &mdash; 250 guests, expanded sponsor wall, live media,
            and a night the industry won't forget.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {GALLERY_2024.map((g, i) => (
            <ArchiveCard key={g.url} g={g} year="2024" testId={`gallery-2024-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
